import { TramoVesUsdt, TramosVesUsdtResponse } from '../../services/wallet-ves-client.service';
import { letraTramo, TOLERANCIA_TASA, tramoDelMonto, tramosValidos } from '../convert-ves-usdt/convert-ves-usdt.tramos';

/**
 * Tramos de USDT→VES (21/10/2026): el espejo de `convert-ves-usdt.tramos.ts`.
 * Funciones puras, sin Angular. Ante cualquier falta de datos se paga la base,
 * como antes.
 *
 * El cliente VENDE USDT y recibe `monto × tasa` VES: una tasa más ALTA es
 * mejor para él. Mismas reglas que el back (`cotizarUsdtVes`):
 *   - el tramo se elige directo por los USDT que vende (no hay conversión);
 *   - es el más alto cuyo «desde» no supera el monto; bajo el primero, el primero.
 * El back acepta la tasa enviada con 1 % de tolerancia contra la base o la del
 * tramo, así que mandar la base (cuando no hay tramos) sigue siendo válido.
 *
 * Los estados de lectura (fallos seguidos, vigencia) son los mismos de
 * VES→USDT: se reusan `registrarLectura` / `tramosParaCotizar` desde allá.
 */

export interface TasaVentaTramo {
  /** VES por USDT a pagar. Sin tramo que mejore, la base de la pantalla. */
  tasa: number;
  /** Null cuando no hay tramos, la llamada falló o el tramo no mejora la base. */
  tramo: TramoVesUsdt | null;
  /** 'A', 'B', 'C'… por posición. Vacío si no hay tramo que mejore. */
  letra: string;
  /** VES de más frente a vender los mismos USDT a la base. */
  extraVes: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * ¿Se avisa "La tasa ha cambiado"? Mismo criterio que VES→USDT
 * (`avisarCambioDeTasa`): solo si empeoró para el cliente más que la
 * tolerancia. Acá el cliente VENDE USDT y recibe VES, así que empeora cuando
 * la tasa BAJA (menos VES por USDT). Si sube, se actualiza sin aviso.
 */
export const avisarBajaDeTasaVenta = (
  anterior: number,
  nueva: number,
  tolerancia = TOLERANCIA_TASA,
): boolean => anterior > 0 && anterior - nueva > tolerancia;

const sinTramo = (tasaBase: number): TasaVentaTramo => ({ tasa: tasaBase, tramo: null, letra: '', extraVes: 0 });

/**
 * Tasa para vender `montoUsdt`. `tasaBase` es la `tasaFinal` de hoy
 * (`rates/usdt-ves/rate`). Un tramo que no supera la base (el chico, o datos
 * desfasados) no cambia nada: se paga la base de la pantalla, la de siempre.
 */
export const tasaVentaParaMontoUsdt = (
  data: TramosVesUsdtResponse | null | undefined,
  montoUsdt: number,
  tasaBase: number,
): TasaVentaTramo => {
  try {
    const base = Number(tasaBase);
    if (!(base > 0)) return sinTramo(tasaBase);
    const tramos = tramosValidos(data);
    if (!tramos.length) return sinTramo(base);
    const usdt = Number(montoUsdt) || 0;
    const tramo = tramoDelMonto(tramos, usdt);
    if (!tramo) return sinTramo(base);
    const tasa = Number(tramo.tasa);
    // Solo mejora: el tramo tiene que pagar más que la base (con tolerancia).
    if (!(tasa - base > TOLERANCIA_TASA)) return sinTramo(base);
    return {
      tasa,
      tramo,
      letra: letraTramo(tramos.indexOf(tramo)),
      extraVes: usdt > 0 ? round2(usdt * (tasa - base)) : 0,
    };
  } catch {
    return sinTramo(tasaBase);
  }
};
